import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { Repeat } from "lib/@components/repeat"
import { If, Else } from "lib/@components/switch"
import { useDialog } from "lib/@hooks/useDialog"
import { confirm } from "lib/@dialogs/confirm"
import { useSnackbar } from "notistack"
import { getTranslationLanguageStats } from "routes/schedule/plugins/translation/controller/get-translation-language-stats"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { clearTranslationCache } from "routes/schedule/plugins/translation/controller/clear-translation-cache"
import { removeContentLanguage } from "routes/schedule/plugins/translation/controller/remove-content-language"
import { AddLanguageDialog } from "routes/schedule/plugins/translation/add-language-dialog"
import { LanguageStatRow } from "./language-stat-row"

export function TranslationManagementPanel() {
    const stats = getTranslationLanguageStats.useResults(withInitialValue(IS_LOADING))
    const openAddDialog = useDialog(<AddLanguageDialog />)
    const { enqueueSnackbar } = useSnackbar()

    if (stats === IS_LOADING) return <LoadingScreen />

    return (
        <Box p={3}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Translation Languages</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage content languages and view translation cache status.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Iconify icon="mdi:plus" />} onClick={handleAddLanguage}>
                    Add Language
                </Button>
            </Box>

            <If value={stats.length}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Language</TableCell>
                                <TableCell>Code</TableCell>
                                <TableCell>Direction</TableCell>
                                <TableCell align="right">Cached Schedules</TableCell>
                                <TableCell align="center">Skills</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat
                                list={stats}
                                item={<LanguageStatRow onClearCache={handleClearCache} onRemove={handleRemove} />}
                            />
                        </TableBody>
                    </Table>
                </TableContainer>
                <Else>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                        No languages configured. Add a language to get started.
                    </Typography>
                </Else>
            </If>
        </Box>
    )

    async function handleAddLanguage() {
        await openAddDialog()
    }

    async function handleClearCache(lang) {
        const ok = await confirm(
            `Clear all cached translations for ${lang.name}? Schedules will be re-translated on next view.`,
            "Clear Cache"
        )
        if (!ok) return
        await clearTranslationCache({ language: lang.code })
        enqueueSnackbar(`Cache cleared for ${lang.name}`, { variant: "success" })
    }

    async function handleRemove(lang) {
        const ok = await confirm(`Remove ${lang.name}? This will also clear its translation cache.`, "Remove Language")
        if (!ok) return
        await clearTranslationCache({ language: lang.code })
        await removeContentLanguage({ code: lang.code })
        enqueueSnackbar(`${lang.name} removed`, { variant: "success" })
    }
}
